import React, { useState } from "react";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import "../styles/ImageComponent.scss";

const ImageComponent = ({ content }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  let imageData = content;

  if (typeof content === "string") {
    try {
      imageData = JSON.parse(content);
    } catch (e) {
      console.error("Error parsing image data:", e);
      return <div>Error loading image</div>;
    }
  }

  const imageUrl =
    imageData?.url || imageData?.src || imageData?.imageUrl || "";

  if (!imageUrl) {
    console.error("No image URL found in content:", imageData);
    return <div>No image URL provided</div>;
  }

  const handleImageClick = () => {
    setZoomLevel(1);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setZoomLevel(1);
    setIsModalOpen(false);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.25 : 0.25;
      setZoomLevel((prev) => Math.min(Math.max(prev + delta, 1), 3));
    }
  };

  const handleImageDoubleClick = () => {
    setZoomLevel((prev) => (prev === 1 ? 2 : 1));
  };

  const getMaterialType = (material) => {
    if (!material) return "image";

    const content = material.content || material.url || material.src || "";

    if (typeof content === "string") {
      if (content.includes("youtube.com") || content.includes("youtu.be")) {
        return "video";
      }
      if (content.includes(".pdf")) {
        return "pdf";
      }
      if (
        content.includes(".mp4") ||
        content.includes(".webm") ||
        content.includes(".ogg")
      ) {
        return "video";
      }
    }

    return "image";
  };

  const getMaterialLink = (material) => {
    return material?.content || material?.url || material?.src || "";
  };

  return (
    <>
      <div className="image-component">
        <div className="image-wrapper" onClick={handleImageClick}>
          <img
            src={imageUrl}
            alt={
              imageData.content ||
              imageData.alt ||
              imageData.caption ||
              "Anatomy illustration"
            }
            className="anatomy-image"
          />
          <div className="zoom-overlay">
            <ZoomOutMapIcon className="zoom-icon" />
          </div>
        </div>
        {(imageData.content || imageData.caption) && (
          <p className="image-caption">
            {imageData.content || imageData.caption}
          </p>
        )}
      </div>

      {isModalOpen && (
        <div className="materials-modal" onClick={handleCloseModal}>
          <div
            className="materials-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="materials-modal__close-wrapper">
              <div className="materials-title">
                {/* {imageData.name || imageData.title || "Media"} */}
              </div>
              <div>
                {getMaterialType(imageData) === "image" && (
                  <div className="materials-modal__controls">
                    <button
                      type="button"
                      className="zoom-btn"
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                      title="Zoom In"
                    >
                      <ZoomInIcon />
                    </button>
                    <span className="zoom-level">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="materials-modal__close"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>
            {(() => {
              const type = getMaterialType(imageData);
              const link = getMaterialLink(imageData);
              const name = imageData.name || imageData.title || "";

              const isVideo = type === "video";
              const isPdf = type === "pdf";

              const isYouTubeLink =
                typeof link === "string" &&
                (link.includes("youtube.com") || link.includes("youtu.be"));

              let youtubeEmbedUrl = null;
              if (isYouTubeLink) {
                try {
                  if (link.includes("youtube.com/watch")) {
                    const url = new URL(link);
                    const v = url.searchParams.get("v");
                    if (v) {
                      youtubeEmbedUrl = `https://www.youtube.com/embed/${v}`;
                    }
                  } else if (link.includes("youtu.be/")) {
                    const id = link.split("youtu.be/")[1]?.split(/[?&#]/)[0];
                    if (id) {
                      youtubeEmbedUrl = `https://www.youtube.com/embed/${id}`;
                    }
                  }
                } catch (e) {
                  youtubeEmbedUrl = null;
                }
              }

              return (
                <div className="materials-modal__body" onWheel={handleWheel}>
                  {isVideo && link && youtubeEmbedUrl && (
                    <iframe
                      title={name || "Video"}
                      src={youtubeEmbedUrl}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  )}

                  {isVideo && link && !youtubeEmbedUrl && (
                    <video
                      controls
                      style={{
                        width: "100%",
                        maxHeight: "70dvh",
                        borderRadius: 8,
                      }}
                    >
                      <source src={link} />
                      Your browser does not support the video tag.
                    </video>
                  )}

                  {isPdf && link && link.toLowerCase().includes(".pdf") && (
                    <iframe
                      title={name || "PDF"}
                      src={link}
                      style={{
                        width: "100%",
                        height: "70dvh",
                        border: "none",
                      }}
                    />
                  )}

                  {isPdf && link && !link.toLowerCase().includes(".pdf") && (
                    <div>
                      <p>This material is an invalid link.</p>
                    </div>
                  )}

                  {!isVideo && !isPdf && link && (
                    <div
                      className="image-container"
                      style={{ overflow: "auto", height: "70dvh" }}
                    >
                      <img
                        src={link}
                        alt={name || "Image"}
                        onDoubleClick={handleImageDoubleClick}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transformOrigin: "center",
                          transition: "transform 0.2s ease",
                          cursor: zoomLevel > 1 ? "move" : "pointer",
                        }}
                      />
                    </div>
                  )}

                  {!link && (
                    <div>No content URL configured for this material.</div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageComponent;
